import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { isAxiosError } from 'axios';
import { GetApiDoctorsPortalDoctors200, GetApiDoctorsPortalDoctorsStatus } from '@/shared/api/models';
import { api } from '@/utils/Api/POS';
import { setDoctorsList, setDoctorsLoading, setDoctorsError } from '@/redux/slices/Doctors';

export interface UseFetchDoctorsListParams {
    page: number;
    limit: number;
    keyword?: string;
    status?: GetApiDoctorsPortalDoctorsStatus;
}

const emptyResponse = (limit: number): GetApiDoctorsPortalDoctors200 => ({
    doctors: [],
    currentPage: 1,
    totalPages: 0,
    limit,
    total: 0,
});

/**
 * Fetches doctors list from API and dispatches result to Redux.
 * Call when on /doctors route; refetch when params change or when route is hit.
 */
export function useFetchDoctorsList(params: UseFetchDoctorsListParams) {
    const dispatch = useDispatch();
    const { page, limit, keyword, status } = params;
    const paramsRef = useRef(params);
    paramsRef.current = params;

    const fetchDoctors = useCallback(async () => {
        const { page: p, limit: l, keyword: k, status: s } = paramsRef.current;
        dispatch(setDoctorsLoading(true));
        try {
            const res = await api.getApiDoctorsPortalDoctors({
                page: p,
                limit: l,
                keyword: k?.trim() || undefined,
                status: s,
            });
            dispatch(
                setDoctorsList({
                    doctors: res.doctors,
                    currentPage: res.currentPage,
                    totalPages: res.totalPages,
                    limit: res.limit,
                    total: res.total,
                }),
            );
        } catch (error) {
            if (isAxiosError(error) && error.response?.status === 404) {
                dispatch(setDoctorsList(emptyResponse(l)));
            } else {
                dispatch(setDoctorsError(error instanceof Error ? error.message : 'Failed to fetch doctors'));
            }
        }
    }, [dispatch]);

    useEffect(() => {
        fetchDoctors();
    }, [page, limit, keyword, status, fetchDoctors]);

    return { refetch: fetchDoctors };
}
